import React from 'react';

import {assert} from 'Asserts';
import {MinimalFolder} from 'constants/FolderConstants';
import {PROJECT_TYPE} from 'constants/SourceConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import FolderDAO from 'file-browser/FolderDAO';
import FolderSelectTree from 'file-browser/FolderSelectTree';
import {FilterKey, GenericFilter} from 'search/SearchConstants';
import {useAsyncStateOnMount, AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import MenuPopover from 'ui-library/menus/MenuPopover';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import PrimaryFilterFormSkeleton from './base/PrimaryFilterFormSkeleton';

interface FolderFilterFormProps extends BaseFilterFormProps {
  filter: GenericFilter<FilterKey.FOLDER>;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: GenericFilter<FilterKey.FOLDER>) => void;
  onRemove?: () => void;
}

export interface FolderFilterFormExtraProps {
  onlyProjects?: boolean;
}

export default function FolderFilterForm(
  props: FolderFilterFormProps & FolderFilterFormExtraProps
): JSX.Element {
  const asyncFoldersState = useAsyncStateOnMount(() => Promise.resolve(FolderDAO.fetchAll({level: 'read'})));

  function _getSelectableFolders(): Array<MinimalFolder> | null {
    let selectableFolders: Array<MinimalFolder> | null = null;
    if (asyncFoldersState.status === AsyncStatus.SUCCESS && asyncFoldersState.data != null) {
      selectableFolders = asyncFoldersState.data;
    }

    if (selectableFolders && props.onlyProjects) {
      const isProjectSourcePredicate = (folder: MinimalFolder): boolean =>
        Boolean(folder.source && folder.source.sourceType === PROJECT_TYPE);
      const isProjectRootFolderPredicate = FileBrowserUtil.propsToFolderPredicate({
        showInventory: false,
        showNotebook: false,
      });
      selectableFolders = selectableFolders.filter(
        (folder: MinimalFolder) => isProjectSourcePredicate(folder) && isProjectRootFolderPredicate(folder)
      );
    }
    return selectableFolders;
  }

  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const selectableFolders = _getSelectableFolders();
  const folderSelectTree =
    selectableFolders != null ? (
      <FolderSelectTree
        allowNone={true}
        className="u-no-spacing-bottom folderFilter-selectTree"
        folders={selectableFolders}
        folderIds={props.filter.value ? [String(props.filter.value)] : []}
        onChangeSelectedFolders={(folderIds: Array<string>) => {
          assert(folderIds.length <= 1, 'Expected 1 or fewer folders');
          props.onChangeFilter({
            ...props.filter,
            value: folderIds.length ? folderIds[0] : null,
          });
        }}
        multiple={false}
      />
    ) : (
      <CenteredHorizontalThrobber />
    );
  if (props.filterStyle !== FilterStyle.PRIMARY) {
    throw new Error('FolderFilterForm not yet implemented as secondary filter');
  }
  return (
    <PrimaryFilterFormSkeleton
      {...skeletonProps}
      buttonSize={props.buttonSize}
      menuOverride={<MenuPopover>{folderSelectTree}</MenuPopover>}
      dropdownToggleText={props.onlyProjects ? 'Project' : props.dropdownToggleText}
    />
  );
}
